import { useCallback, useEffect, useRef } from "react";
import { useSlideStore } from "../store/useSlideStore";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import { useRuntimeContext } from "../context/RuntimeContext";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useStudentInputStore } from "../store/useStudentInputStore";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  StudentInteractionTypes,
  trackStudentInteraction,
} from "@/utils/mixpanel-utils";
import { getRemainingDuration } from "@/utils/audioUtils";
import { useCooldown } from "../context/CooldownContext";
import { useNotepadStore } from "@/features/notepad/store/useNotepadStore";
import { useNotepadPerSlideStore } from "@/features/notepad/store/useNotepadPerSlideStore";
import { CANVAS_GATEWAY_CONFIGURED } from "@/flags";
import { restorePagesFromSession } from "@/features/notepad/utils/restorePagesFromSession";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { handleLessonEnd } from "../utils/endLesson";
import { executeSlideTransition } from "../utils/slideTransition";
import {
  applyInputRevealedDOM,
  applyInputUnrevealedDOM,
} from "../utils/inputRevealDomHelpers";
import { addResumeDebugEvent } from "@/store/useResumeDebugStore";
import { debugLog, debugWarn } from "@/utils/debugLog";
import { TutorMode } from "@/constants/mode";

const useHandleTutorAction = ({ mode = TutorMode.PUBLIC, lessonId } = {}) => {
  const { setIsScratchPadOpen, setActiveAssessmentModal } = useUIStore();
  const { emit } = useRuntimeContext();
  const slidesLength = useSlideStore((state) => state.slides?.length ?? 0);
  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { resetCooldown, releaseCooldown } = useCooldown();
  // A Set: end_lesson and next_slide can both be pending within one turn.
  const pendingTerminalTimersRef = useRef(new Set());

  useEffect(() => {
    const pendingTimers = pendingTerminalTimersRef.current;
    return () => {
      pendingTimers.forEach(clearTimeout);
      pendingTimers.clear();
    };
  }, []);

  // Schedule an action that leaves the slide, against the audio still buffered
  // for this turn. The turn id is revalidated when the timer fires: a barge-in
  // or a manual navigation mints a new turn, and this one is no longer owed.
  const scheduleTerminalAction = useCallback(
    (delayMs, turnIdAtAction, run, { onStale } = {}) => {
      const timerId = setTimeout(async () => {
        pendingTerminalTimersRef.current.delete(timerId);
        if (
          turnIdAtAction &&
          useTurnStore.getState().getTurnId() !== turnIdAtAction
        ) {
          debugWarn(
            `Dropping terminal tutor action from stale turn ${turnIdAtAction}`,
          );
          addResumeDebugEvent("tutor_action:terminal:dropped_stale_turn", {
            turn_id: turnIdAtAction,
          });
          onStale?.();
          return;
        }
        await run();
      }, delayMs);
      pendingTerminalTimersRef.current.add(timerId);
    },
    [],
  );

  const handleTutorAction = useCallback(
    async (action) => {
      // Check turn_id before processing the action
      if (action.turn_id) {
        const currentTurnId = useTurnStore.getState().getTurnId();
        if (action.turn_id !== currentTurnId) {
          debugWarn(
            `Ignoring tutor action with turn_id ${action.turn_id} - not the current turn ${currentTurnId}`,
          );
          return;
        }
      }

      const tutorAction = action.tutor_action;

      if (tutorAction.open_notepad === true) {
        if (!CANVAS_GATEWAY_CONFIGURED) return;
        const notepadState = useNotepadStore.getState();

        // Only enforce once-per-slide restriction in limited mode
        if (
          notepadState.isLimitedMode &&
          useNotepadPerSlideStore.getState().flags[
            useSlideStore.getState().currentSlideIndex
          ] === true
        ) {
          debugLog(
            "[useHandleTutorAction] Ignoring open_notepad — limited mode, already used on this slide",
          );
          return;
        }

        // Create a new page before showing the toast.
        // Only add a page when the notepad is closed — if it's already open
        // (e.g. duplicate open_notepad action), addPage() would change
        // currentPageIndex and cause the TldrawCanvasAgent to remount
        // mid-request, producing a "Transport disposed" error.
        if (notepadState.state === "CLOSED") {
          restorePagesFromSession();
          useNotepadStore.getState().addPage();
          // Persist the updated page list to sessionStorage
          try {
            const sessionId = useSessionStore.getState().getSessionId?.();
            const slideIndex = useSlideStore.getState().currentSlideIndex;
            if (sessionId != null && slideIndex != null) {
              const slideKey = `${sessionId}-${slideIndex}`;
              const updatedPages = useNotepadStore.getState().pages;
              sessionStorage.setItem(
                `canvas_pages_${slideKey}`,
                JSON.stringify(updatedPages),
              );
            }
          } catch {
            // best-effort
          }
        }

        useNotepadStore.getState().showToast();
        return;
      }

      if (
        tutorAction.end_lesson !== undefined &&
        tutorAction.end_lesson === true
      ) {
        const remainingDuration = getRemainingDuration();
        scheduleTerminalAction(
          remainingDuration * 1000,
          action.turn_id,
          async () => {
            await handleLessonEnd(navigate, searchParams, {
              skipModalForExitTicket: true,
              lessonId,
            });
          },
        );
        return;
      }

      if (tutorAction.fragment_ids && Array.isArray(tutorAction.fragment_ids)) {
        addResumeDebugEvent("tutor_action:fragments:start", {
          fragment_ids: tutorAction.fragment_ids,
          turn_id: action.turn_id,
        });
        const delayInSeconds = tutorAction.delay_in_seconds || 0.5;
        const delayInMs = delayInSeconds * 1000;

        // Track max fragment to reveal all inputs up to that point (for jump-ahead)
        let maxFragmentIndex = -1;

        for (let i = 0; i < tutorAction.fragment_ids.length; i++) {
          const fragmentIndex = tutorAction.fragment_ids[i];

          if (typeof fragmentIndex === "number") {
            // Track the highest fragment we navigate to
            if (fragmentIndex > maxFragmentIndex) {
              maxFragmentIndex = fragmentIndex;
            }

            // Get current deck directly from store
            const currentDeck = useSlideStore.getState().currentDeck;

            if (
              currentDeck &&
              typeof currentDeck.navigateFragment === "function"
            ) {
              addResumeDebugEvent("tutor_action:navigate_fragment", {
                fragment_index: fragmentIndex,
              });
              currentDeck.navigateFragment(fragmentIndex);
            } else {
              console.error(
                "currentDeck is null or navigateFragment method not available",
              );
              addResumeDebugEvent(
                "tutor_action:navigate_fragment:missing_deck",
                {
                  fragment_index: fragmentIndex,
                },
              );
            }

            if (i < tutorAction.fragment_ids.length - 1 && delayInMs > 0) {
              await new Promise((resolve) => setTimeout(resolve, delayInMs));
            }
          }
        }

        // After navigating all fragments, reveal ALL inputs whose answer_fragment_index <= max fragment
        // This handles jump-ahead scenarios where intermediate inputs should also be cleared
        if (maxFragmentIndex >= 0) {
          // First, get inputs that need to be unrevealed (backward navigation)
          // We need this list BEFORE revealing to avoid flicker
          const actionIdsToUnreveal = useSlideStore
            .getState()
            .getInputsToUnrevealAboveFragment(maxFragmentIndex);
          const unrevealSet = new Set(actionIdsToUnreveal);

          // Unreveal inputs whose answer_fragment_index > maxFragmentIndex (backward navigation)
          // Do this FIRST to avoid flicker
          actionIdsToUnreveal.forEach((actionId) => {
            const input = document.querySelector(
              `input[data-action-id="action_${actionId}"]`,
            );
            if (input) {
              const inputKey = input.dataset.inputKey;
              if (
                inputKey &&
                useStudentInputStore.getState().revealedInputs.has(inputKey)
              ) {
                // Update state (pure) then apply DOM changes (side effect)
                useStudentInputStore.getState().unmarkInputRevealed(inputKey);
                applyInputUnrevealedDOM(`action_${actionId}`);
              }
            }
          });

          // Now reveal inputs whose answer_fragment_index <= maxFragmentIndex
          // Skip any that are in the unreveal set (shouldn't happen, but safety check)
          const actionIdsToReveal = useSlideStore
            .getState()
            .getInputsToRevealUpToFragment(maxFragmentIndex);

          actionIdsToReveal.forEach((actionId) => {
            // Skip if this input should be unrevealed
            if (unrevealSet.has(actionId)) return;

            const input = document.querySelector(
              `input[data-action-id="action_${actionId}"]`,
            );
            if (input) {
              const inputKey = input.dataset.inputKey;
              if (inputKey) {
                // Update state (pure) then apply DOM changes (side effect)
                useStudentInputStore.getState().markInputRevealed(inputKey);
                applyInputRevealedDOM(`action_${actionId}`);
              }
            }
          });
        }
        return;
      } else if (tutorAction.next_slide_idx !== undefined) {
        // Presence only — the value is deliberately unused. It is an unvalidated
        // LLM tool field, and it indexes the backend's slide list, which omits
        // assessment slides the frontend array carries.
        const remainingDuration = getRemainingDuration();
        const slideIndexAtAction = useSlideStore.getState().currentSlideIndex;
        scheduleTerminalAction(
          remainingDuration * 1000,
          action.turn_id,
          async () => {
            // Here, not when the action arrived: an early reset leaves the
            // button clickable for the rest of the audio and the countdown.
            resetCooldown();
            await executeSlideTransition({
              emit,
              navigate,
              searchParams,
              setSearchParams,
              setActiveAssessmentModal,
              persistSlideIndexInUrl: mode === TutorMode.PREVIEW,
            });
          },
          {
            // Dropped because the student spoke up. The tutor still judged this
            // bite done, so don't make them sit out the rest of the cooldown —
            // but only while they are still on it: a manual navigation also
            // goes stale, and that slide has its own fresh cooldown to keep.
            onStale: () => {
              if (
                useSlideStore.getState().currentSlideIndex ===
                slideIndexAtAction
              ) {
                releaseCooldown();
              }
            },
          },
        );
      }
    },
    [
      setActiveAssessmentModal,
      emit,
      slidesLength,
      currentSlideIndex,
      navigate,
      scheduleTerminalAction,
      searchParams,
      resetCooldown,
      releaseCooldown,
      setIsScratchPadOpen,
      setSearchParams,
      mode,
      lessonId,
    ],
  );
  return { handleTutorAction };
};

export default useHandleTutorAction;
